import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { listMeetings } from '../../api/meetings/meetings';
import MeetingsListView from '../components/list/MeetingsListView';
import { ListRowSkeletonStack } from '../../components/loading/skeletons';
import '../styles/MeetingsPage.css';

const MeetingsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { companyData } = useAuth();

  const [meetings, setMeetings] = useState([]);
  const [cancelledMeetings, setCancelledMeetings] = useState([]);
  const [meetingsReady, setMeetingsReady] = useState(false);
  const [cancelledReady, setCancelledReady] = useState(false);
  const [currentTab, setCurrentTab] = useState(location.state?.initialTab ?? 0);
  const [searchTerm, setSearchTerm] = useState('');
  const loading = !meetingsReady || !cancelledReady;

  useEffect(() => {
    const tab = location.state?.initialTab ?? 0;
    setCurrentTab(tab);
  }, [location.state?.initialTab]);

  useEffect(() => {
    if (!companyData?.id) {
      setMeetingsReady(true);
      return undefined;
    }

    setMeetingsReady(false);
    let cancelled = false;
    listMeetings({ companyId: companyData.id, archived: false })
      .then((rows) => {
        if (cancelled) return;
        const items = rows
          .map((data) => {
            const dateTime = data.dateTime instanceof Date ? data.dateTime : data.dateTime ? new Date(data.dateTime) : null;
            if (!dateTime || isNaN(dateTime.getTime())) return null;
            const status = (data.status || '').toLowerCase();
            const isCancelled = status === 'cancelled' || status === 'canceled';
            const isArchived = Boolean(data.archived || data.archivedAt);
            if (isCancelled || isArchived) return null;
            return { ...data, dateTime };
          })
          .filter(Boolean)
          .sort((a, b) => a.dateTime - b.dateTime);
        setMeetings(items);
        setMeetingsReady(true);
      })
      .catch(() => {
        if (!cancelled) setMeetingsReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [companyData?.id]);

  useEffect(() => {
    if (!companyData?.id) {
      setCancelledReady(true);
      return undefined;
    }

    setCancelledReady(false);
    let cancelled = false;
    listMeetings({ companyId: companyData.id, archived: true })
      .then((rows) => {
        if (cancelled) return;
        const items = rows
          .map((data) => {
            const dateValue = data.dateTime || data.rescheduledDateTime || data.originalDateTime;
            const dateTime = dateValue instanceof Date ? dateValue : dateValue ? new Date(dateValue) : null;
            if (!dateTime || isNaN(dateTime.getTime())) return null;
            const status = (data.status || '').toLowerCase();
            if (status !== 'cancelled' && status !== 'canceled') return null;
            return { ...data, dateTime, fromArchive: true };
          })
          .filter(Boolean)
          .sort((a, b) => b.dateTime - a.dateTime);
        setCancelledMeetings(items);
        setCancelledReady(true);
      })
      .catch(() => {
        if (!cancelled) setCancelledReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [companyData?.id]);

  const filteredMeetings = useMemo(() => {
    const sourceMeetings = currentTab === 3 ? cancelledMeetings : meetings;
    if (!sourceMeetings.length) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    return sourceMeetings.filter((meeting) => {
      const date = meeting.dateTime;
      if (!date) return false;

      const normalizedSearch = searchTerm.trim().toLowerCase();
      if (normalizedSearch) {
        const additionalClientText = (meeting.additionalClients || [])
          .map((client) => `${client?.name || ''} ${client?.email || ''}`.trim())
          .join(' ');
        const searchableText = [
          meeting.title,
          meeting.clientName,
          meeting.clientEmail,
          meeting.clientPhone,
          meeting.location,
          meeting.status,
          meeting.meetingType,
          meeting.notes,
          meeting.rescheduleReason,
          meeting.cancelReason,
          additionalClientText,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        if (!searchableText.includes(normalizedSearch)) {
          return false;
        }
      }

      switch (currentTab) {
        case 0: // All
          return true;
        case 1: // Today
          return date >= today && date < tomorrow;
        case 2: // Pending
          return (meeting.status || '').toLowerCase() === 'pending';
        case 3: // Cancelled
          return true;
        default:
          return false;
      }
    });
  }, [meetings, cancelledMeetings, currentTab, searchTerm]);

  return (
    <div className="meetings-page">
      <div className="meetings-page-toolbar">
        <nav className="meetings-page-tabs" role="tablist" aria-label="Filter meetings">
          <ul className="meetings-page-tabs__list">
            <li className="meetings-page-tabs__tab" role="presentation">
              <button
                type="button"
                role="tab"
                aria-selected={currentTab === 0}
                aria-controls="meetings-panel-0"
                id="meetings-tab-0"
                className="meetings-page-tabs__btn"
                onClick={() => setCurrentTab(0)}
              >
                All
              </button>
            </li>
            <li className="meetings-page-tabs__tab" role="presentation">
              <button
                type="button"
                role="tab"
                aria-selected={currentTab === 1}
                aria-controls="meetings-panel-1"
                id="meetings-tab-1"
                className="meetings-page-tabs__btn"
                onClick={() => setCurrentTab(1)}
              >
                Today
              </button>
            </li>
            <li className="meetings-page-tabs__tab" role="presentation">
              <button
                type="button"
                role="tab"
                aria-selected={currentTab === 2}
                aria-controls="meetings-panel-2"
                id="meetings-tab-2"
                className="meetings-page-tabs__btn"
                onClick={() => setCurrentTab(2)}
              >
                Pending
              </button>
            </li>
            <li className="meetings-page-tabs__tab" role="presentation">
              <button
                type="button"
                role="tab"
                aria-selected={currentTab === 3}
                aria-controls="meetings-panel-3"
                id="meetings-tab-3"
                className="meetings-page-tabs__btn"
                onClick={() => setCurrentTab(3)}
              >
                Cancelled
              </button>
            </li>
          </ul>
        </nav>

        <div className="meetings-page-search">
          <input
            type="search"
            className="meetings-page-search__input"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search meetings"
            aria-label="Search meetings"
          />
        </div>
      </div>

      {loading ? (
        <ListRowSkeletonStack rows={6} variant="upcoming" label="Loading meetings" />
      ) : filteredMeetings.length ? (
        <div className="meetings-page-list">
          <MeetingsListView
            meetings={filteredMeetings}
            onMeetingClick={(m) => {
              const meetingSegment = m.shortSlug ?? m.id;
              navigate(
                `/app/overview/meeting/${meetingSegment}${m.fromArchive ? '?archived=1' : ''}`,
                {
                  state: {
                    meeting: m,
                    ...(m.fromArchive ? { fromArchive: true } : {}),
                  },
                }
              );
            }}
          />
        </div>
      ) : (
        <p className="meetings-page-empty">No meetings found</p>
      )}
    </div>
  );
};

export default MeetingsPage;
